import { apiErrorCode, apiErrorReason, apiErrorRecording } from './apiErrorCode';
import { clock } from './clock';

/**
 * Минимум для открытых заданий (решение владельца 10.09.2026).
 *
 * ⚠️ СЧИТАЕМ ТАК ЖЕ, КАК СЕРВЕР (договор `empty-open-answers.md`). Сочинение
 * пустое, если в нём нет ни одной буквы или цифры: «…» и «— — —» — не ответ,
 * хотя по пробелам это «слова». Запись — не короче 5 секунд; сервер меряет файл
 * сам и проверит всё равно, клиент лишь не даёт отправить заведомо короткое.
 */

export const MIN_RECORDING_SECONDS = 5;

export function essayWordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Можно ли сохранить сочинение: есть буква или цифра и слов не меньше минимума (но хотя бы одно). */
export function essayCanSave(text: string, minWords = 0): boolean {
  return /[\p{L}\p{N}]/u.test(text) && essayWordCount(text) >= Math.max(1, minWords);
}

/**
 * Запись короче минимума. Меряем миллисекунды: 4,5 секунды округлились бы до 5
 * и прошли бы клиент, а сервер их отверг бы.
 */
export function recordingTooShort(ms: number): boolean {
  return ms < MIN_RECORDING_SECONDS * 1000;
}

/**
 * Что не так с сохранённым открытым ответом, пришедшим с сервера (11.09.2026, 50 учеников на проде).
 *
 * Запись короче 5 секунд, сохранённая до правила, показывалась зелёным «Сохранено», а
 * раздел не закрывался. Судим по двум признакам:
 * - `namedByServer` — задание есть в `block_ids` отказа `409 open_answers_missing`: сервер
 *   меряет файл сам, его слово главное;
 * - `durationSec` — длительность, которую насчитал проигрыватель. Пока она неизвестна
 *   (`null`), не судим: «Сохранено» остаётся, решит сервер.
 *
 * ⚠️ Отказ 409 приходит только на отметку раздела. У домашнего раздела её нет, поэтому
 * без длительности проигрывателя короткая запись в домашке так и осталась бы зелёной.
 */
export function savedOpenAnswerProblem(input: {
  type: string;
  answer: string;
  durationSec?: number | null;
  namedByServer?: boolean;
}): 'audio_too_short' | 'essay_empty' | 'open_items_empty' | null {
  const { type, answer, durationSec, namedByServer } = input;
  if (type === 'essay') {
    return namedByServer || !essayCanSave(answer, 0) ? 'essay_empty' : null;
  }
  // «Ответ по пунктам»: правило «все пункты заполнены» знает блок (lib/openItems.ts) — здесь только слово сервера.
  if (type === 'open_items') return namedByServer ? 'open_items_empty' : null;
  if (type === 'audio_record') {
    if (namedByServer || !answer.trim()) return 'audio_too_short';
    if (durationSec != null && Number.isFinite(durationSec) && recordingTooShort(durationSec * 1000)) {
      return 'audio_too_short';
    }
    return null;
  }
  return null;
}

/** Ключ строки для ученика и подстановки к ней. */
export type OpenAnswerRejection = { key: string; params?: Record<string, string> };

/**
 * Отказ сервера → что сказать ученику (12.09.2026, договор `record-integrity.md` §2.3).
 *
 * 🔴 ЗАЧЕМ ПОДРОБНОСТИ. С 12.09 сервер различает четыре беды записи, а фронт на
 * все отвечал одним «Запись слишком короткая — запиши не короче 5 секунд». Две
 * минуты речи в отключённый микрофон — и совет записать подлиннее: ученик делает
 * то же самое ещё раз и упирается снова.
 *
 * ⚠️ РАЗЛИЧАЕМ ПО `code` И `reason`. `message` сервера — для человека и может
 * меняться; один код `empty_recording` покрывает четыре разные причины.
 *
 * ⚠️ НЕЗНАКОМАЯ ПРИЧИНА НЕ ОСТАЁТСЯ БЕЗ СЛОВ. Сервер заведёт новую раньше, чем
 * мы о ней узнаем, — тогда общий текст «Запись не сохранилась» лучше пустоты.
 */
export function openAnswerRejection(err: unknown): OpenAnswerRejection | undefined {
  const code = apiErrorCode(err);
  switch (code) {
    case 'essay_empty':
      return { key: 'blockRenderers.essayEmptyServer' };
    case 'open_items_empty':
      return { key: 'blockRenderers.openItemsEmptyServer' };
    case 'audio_too_short':
      return { key: 'blockRenderers.audioTooShortServer' };
    case 'recording_truncated': {
      const rec = apiErrorRecording(err);
      // Числа показываем, только если известны ОБА: «сохранилось 0:20 из 0:00»
      // хуже, чем текст без чисел.
      if (rec && typeof rec.client_seconds === 'number' && Number.isFinite(rec.client_seconds)) {
        return {
          key: 'blockRenderers.recordTruncatedServer',
          params: { saved: clock(rec.server_seconds), total: clock(rec.client_seconds) },
        };
      }
      return { key: 'blockRenderers.recordTruncatedPlain' };
    }
    case 'empty_recording':
      switch (apiErrorReason(err)) {
        case 'silent':
          return { key: 'blockRenderers.recordEmptySilent' };
        case 'unreadable':
          return { key: 'blockRenderers.recordEmptyBroken' };
        default:
          return { key: 'blockRenderers.recordEmptyLost' };
      }
    case 'file_empty':
    case 'file_unreadable':
      return { key: 'blockRenderers.recordEmptyBroken' };
    // Опоздавшая запись из очереди (договор `audio-upload.md` §3.1): слова — из договора.
    case 'answer_superseded':
      return { key: 'blockRenderers.recordLateSuperseded' };
    case 'already_reviewed':
      return { key: 'blockRenderers.recordLateReviewed' };
    // Файла записи на телефоне больше нет (`lib/lessonAudio.ts`) — слова те же, что у «файла нет на сервере».
    case 'record_file_lost':
      return { key: 'blockRenderers.recordEmptyLost' };
    default:
      return undefined;
  }
}

/** Прежняя форма: только ключ. Ею пользуется сочинение, где подстановок нет. */
export function openAnswerRejectionKey(err: unknown): string | undefined {
  return openAnswerRejection(err)?.key;
}
